import clsx from 'clsx'
import { useCallback } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { generateOfferPageRoom, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import styled from 'styled-components'
import { rem } from 'polished'

export const EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT = 36
export const EXPANDED_EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT = 60

const BannerMessage = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: start;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BannerEmphasis = styled.strong`
  display: inline;
`

const BannerLink = styled(TextLink)`
  align-self: auto;
  flex-shrink: 0;
  margin-inline-start: auto;
`

const EmbeddedBannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;
  font-size: ${rem(14)};
  line-height: ${rem(20)};
`

const EmbeddedBanner = styled.div`
  box-sizing: border-box;
  height: ${rem(EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT)};
  padding: ${rem(8)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.lightContrast};

  &.isExpanded {
    height: ${rem(EXPANDED_EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT)};

    ${EmbeddedBannerContent} {
      align-items: flex-end;
    }

    ${BannerMessage} {
      flex: 0 1 auto;
      overflow: visible;
      text-overflow: clip;
      white-space: normal;
    }

    ${BannerEmphasis} {
      display: block;
      margin-bottom: ${rem(4)};
    }

    ${BannerLink} {
      align-self: flex-end;
      margin-inline-start: ${rem(48)};
    }
  }

  &.isRebrand {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

interface Props {
  isExpanded?: boolean
}

function TravellingSoloBanner({ isExpanded = false }: Props) {
  const isRebrand = useRebrandVersion() === 'rebrand'
  const offerPageDispatch = useOfferPageDispatch()

  const handleViewSoloPackages = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    offerPageDispatch({
      type: OfferPageActions.setRooms,
      rooms: [generateOfferPageRoom({ adults: 1, children: 0, infants: 0 })],
    })
  }, [offerPageDispatch])

  return (
    <EmbeddedBanner className={clsx(isExpanded && 'isExpanded', isRebrand && 'isRebrand')}>
      <EmbeddedBannerContent>
        <BannerMessage>
          <FormattedMessage
            id="AfPXtLoQtEvBVDa_Ff5B"
            defaultMessage="<strong>Travelling solo?</strong> Unlock special rates"
            description="Compact promotional banner text encouraging solo travellers to view special rates"
            values={{
              strong: (chunks: React.ReactNode) => <BannerEmphasis key="emphasis">{chunks}</BannerEmphasis>,
            }}
          />
        </BannerMessage>
        <BannerLink underline weight="regular" onClick={handleViewSoloPackages}>
          <FormattedMessage
            id="lPrwzlJE8a8omiMY7tEu"
            defaultMessage="View"
            description="Link text for viewing solo traveller packages from the compact banner"
          />
        </BannerLink>
      </EmbeddedBannerContent>
    </EmbeddedBanner>
  )
}

export default React.memo(TravellingSoloBanner)
